import { Link, useParams } from 'react-router-dom';
import { useSEO, SEOInjection } from '@/hooks/useSEO';
import { articles } from '@/mocks/articleData';
import { newsImageUrls } from '@/mocks/imageUrls';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug] : null;

  if (!article) {
    return <NotFound />;
  }

  const canonicalUrl = `${window.location.origin}/article/${article.slug}`;

  return (
    <>
      <ArticleSEO article={article} canonicalUrl={canonicalUrl} />

      <main>
        <article className="pt-20 md:pt-24">
          <div className="bg-foreground-900">
            <div className="container-custom py-12 md:py-16">
              <div className="max-w-4xl">
                <div className="flex items-center space-x-3 mb-4">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 text-xs font-medium rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {article.category}
                  </Link>
                  <span className="text-sm text-foreground-100/50">{article.date}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-background-50/60 leading-relaxed">
                  {article.excerpt}
                </p>
                <div className="flex items-center space-x-3 mt-6 pt-6 border-t border-foreground-800">
                  <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center">
                    <i className="ri-user-line text-background-50 text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-background-50">{article.author}</p>
                    <p className="text-xs text-background-50/50">{article.authorTitle}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-background-50">
            <div className="container-custom py-10 md:py-14">
              <div className="max-w-4xl mx-auto">
                <div
                  className="prose-custom text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="mt-10 pt-8 border-t border-background-200/60">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-foreground-500 mr-2">标签：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {article.relatedArticles.length > 0 && (
            <section className="bg-background-100">
              <div className="container-custom py-12 md:py-16">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {article.relatedArticles.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 overflow-hidden cursor-pointer"
                    >
                      <div className="relative w-full h-44 overflow-hidden">
                        <img
                          src={newsImageUrls[related.imageSeq] || newsImageUrls['news-summit-2026']}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                          title={related.title}
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-xs text-foreground-400">{related.date}</span>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mt-1 line-clamp-2">
                          {related.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>
    </>
  );
}

function ArticleSEO({ article, canonicalUrl }: { article: { slug: string; title: string; excerpt: string; tags: string[]; imageSeq: string; category: string; categorySlug: string; date: string; author: string }; canonicalUrl: string }) {
  const ogImage = newsImageUrls[article.imageSeq] || newsImageUrls['news-summit-2026'];

  useSEO({
    title: `${article.title} - 豪门国际 | 豪门国际官网`,
    description: article.excerpt,
    keywords: `豪门国际,${article.tags.join(',')},豪门资源,国际资源对接`,
    canonical: canonicalUrl,
    ogTitle: article.title,
    ogDescription: article.excerpt,
    ogImage,
    ogType: 'article',
    ogUrl: canonicalUrl,
    twitterCard: 'summary_large_image',
  });

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '豪门国际',
    },
    url: canonicalUrl,
  };

  return <SEOInjection jsonLd={articleSchema} />;
}